import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../components/admin/layout/AdminLayout';
import InfluencerSNSAddList from '../../components/admin/influencer/InfluencerSNSAddList';
import '../../styles/admin/AdminSummary.css';

const API_URL = process.env.REACT_APP_API_URL;

function buildFallbackUrl(platform, accountName) {
  const name = String(accountName || '').replace(/^@/, '');
  switch (platform) {
    case 'instagram': return `https://www.instagram.com/${name}`;
    case 'tiktok':    return `https://www.tiktok.com/@${name}`;
    case 'youtube':   return `https://www.youtube.com/@${name}`;
    case 'blog':      return `https://www.google.com/search?q=${encodeURIComponent(name + ' 블로그')}`;
    default:          return `https://www.google.com/search?q=${encodeURIComponent(name)}`;
  }
}

function InfluencerSNSAddPage() {
  const [selected, setSelected] = useState(null); // { requestId, ownerId, userId, userName, requestedAt }
  const [detail, setDetail] = useState(null);     // { user, existing[], requested[] }
  const [loading, setLoading] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);

  // 상세 불러오기
  useEffect(() => {
    const id = selected?.requestId;
    if (!id) { setDetail(null); return; }
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/influencer/sns-add/request/${id}`, { credentials: 'include' });
        const data = await res.json();
        if (res.ok && data?.ok) {
          // 요청된 계정에 대한 드래프트 세팅
          const requested = (data.requested || []).map(a => ({
            ...a,
            __draftStatus: ['approved','rejected'].includes(a.status) ? a.status : 'approved',
            __draftFollower: Number.isFinite(Number(a.followerCount)) ? Number(a.followerCount) : 0,
            __draftUrl: a.url || buildFallbackUrl(a.platform, a.accountName),
          }));
          setDetail({ ...data, requested });
        } else {
          setDetail(null);
        }
      } catch {
        setDetail(null);
      }
      setLoading(false);
    })();
  }, [selected]);

  const onChangeSnsStatus = (acc, next) => {
    setDetail(prev => ({
      ...prev,
      requested: (prev?.requested || []).map(a => (a.accountName === acc.accountName && a.platform === acc.platform)
        ? { ...a, __draftStatus: next }
        : a)
    }));
  };
  const onChangeSnsFollower = (acc, v) => {
    const n = Number(v);
    setDetail(prev => ({
      ...prev,
      requested: (prev?.requested || []).map(a => (a.accountName === acc.accountName && a.platform === acc.platform)
        ? { ...a, __draftFollower: Number.isFinite(n) && n >= 0 ? n : '' }
        : a)
    }));
  };
  const onChangeSnsUrl = (acc, v) => {
    setDetail(prev => ({
      ...prev,
      requested: (prev?.requested || []).map(a => (a.accountName === acc.accountName && a.platform === acc.platform)
        ? { ...a, __draftUrl: v }
        : a)
    }));
  };

  const allReady = useMemo(() => {
    const list = detail?.requested || [];
    if (list.length === 0) return false;
    return list.every(a =>
      ['approved','rejected'].includes(a.__draftStatus) &&
      a.__draftFollower !== '' &&
      Number.isFinite(Number(a.__draftFollower)) &&
      Number(a.__draftFollower) >= 0 &&
      (a.__draftStatus === 'approved' ? String(a.__draftUrl || '').trim().length > 0 : true)
    );
  }, [detail]);

  const handleComplete = async () => {
    if (!selected?.requestId) return;
    if (!allReady) { alert('모든 요청 계정의 상태/팔로워 수/URL을 입력해주세요.'); return; }

    const updates = (detail?.requested || []).map(a => ({
      snsId: a.snsId,
      platform: a.platform,
      accountName: a.accountName,
      status: a.__draftStatus,
      followerCount: Number(a.__draftFollower),
      url: String(a.__draftUrl || '').trim()
    }));

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/influencer/sns-add/request/${selected.requestId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ updates })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.ok) {
        setSelected(null);
        setDetail(null);
        setRefreshToken(t => t + 1); // 좌측 목록 갱신(요청 제거)
      } else {
        alert(data?.message || '등록 완료 처리 실패');
      }
    } catch {
      alert('요청 실패');
    }
    setLoading(false);
  };

  const downloadZip = async () => {
    if (!selected?.requestId) return;
    const url = `${API_URL}/api/influencer/sns-add/download-zip?requestId=${encodeURIComponent(selected.requestId)}`;
    window.open(url, '_blank', 'noopener');
  };

  return (
    <AdminLayout title="SNS 등록 추가 요청 관리">
      <div className="admin-grid">
        <div className="admin-section" style={{ height: 'calc(100vh - 120px)' }}>
          <InfluencerSNSAddList
            onSelect={(u) => setSelected(u)}
            selectedId={selected?.requestId}
            refreshToken={refreshToken}
          />
        </div>

        <div className="admin-section admin-section--wide" style={{ height: 'calc(100vh - 120px)', overflowY: 'auto' }}>
          <h3 className="admin-summary__title">SNS 추가 요청 상세</h3>
          {loading && <div className="admin-summary__loading">불러오는 중...</div>}
          {!loading && !detail && <div className="admin-summary__empty" style={{ marginTop: '20px' }}>좌측에서 요청을 선택하세요.</div>}

          {!loading && detail && (
            <>
              <div style={{ lineHeight: 2, fontSize: '14px', color: 'var(--admin-text-body)', marginBottom: '16px' }}>
                <div><strong>인플루언서 ID</strong>: {detail.user?.userId}</div>
                <div><strong>이름</strong>: {detail.user?.userName}</div>
                <div><strong>연락처</strong>: {detail.user?.phoneNumber}</div>
                <div><strong>메일</strong>: {detail.user?.email}</div>
                <div><strong>주소</strong>: {detail.user?.userAddress}</div>
              </div>

              <div style={{ marginTop: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 8, fontSize: '14px', color: 'var(--admin-color-primary)' }}>기존 등록된 SNS 계정(승인됨)</div>
                {(detail.existing || []).length === 0 ? (
                  <div style={{ color: 'var(--admin-text-sub)', fontSize: '14px' }}>기존 승인된 계정 없음</div>
                ) : (
                  <ul style={{ listStyle:'none', padding:0, margin:0 }}>
                    {detail.existing.map(acc => (
                      <li key={acc._id} style={{ padding:'10px 12px', marginBottom: '6px', background: 'var(--admin-bg-body)', borderRadius: '6px', border: '1px solid var(--admin-border-color)', fontSize: '14px' }}>
                        [{acc.platform}] @{acc.accountName} · {Number(acc.followerCount).toLocaleString()} · {acc.url || '-'}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div style={{ marginTop: 20 }}>
                <div style={{ fontWeight: 600, marginBottom: 8, fontSize: '14px', color: 'var(--admin-color-primary)' }}>요청된 계정 처리</div>
                {(detail.requested || []).length === 0 ? (
                  <div style={{ color: 'var(--admin-text-sub)', fontSize: '14px' }}>요청된 계정 없음</div>
                ) : (
                  <ul style={{ listStyle:'none', padding:0, margin:0 }}>
                    {detail.requested.map(acc => (
                      <li key={acc.platform + ':' + acc.accountName} style={{ padding:'12px', marginBottom: '8px', background: 'var(--admin-bg-body)', borderRadius: '6px', border: '1px solid var(--admin-border-color)' }}>
                        <div style={{ display:'grid', gridTemplateColumns:'1fr auto minmax(220px, 320px)', gap:12, alignItems:'center' }}>
                          <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--admin-text-main)' }}>[{acc.platform}] @{acc.accountName}</span>

                          <span>
                            <label style={{ marginRight: 8, fontSize: '14px' }}>
                              <input
                                type="radio"
                                name={`sns-status-${acc.platform}:${acc.accountName}`}
                                value="approved"
                                checked={(acc.__draftStatus || 'approved') === 'approved'}
                                onChange={(e) => onChangeSnsStatus(acc, e.target.value)}
                              /> 승인
                            </label>
                            <label style={{ fontSize: '14px' }}>
                              <input
                                type="radio"
                                name={`sns-status-${acc.platform}:${acc.accountName}`}
                                value="rejected"
                                checked={acc.__draftStatus === 'rejected'}
                                onChange={(e) => onChangeSnsStatus(acc, e.target.value)}
                              /> 반려
                            </label>
                          </span>

                          <span style={{ display:'grid', gridTemplateColumns:'120px 1fr', gap:8, alignItems:'center' }}>
                            <input
                              type="number"
                              min={0}
                              value={acc.__draftFollower}
                              onChange={(e) => onChangeSnsFollower(acc, e.target.value)}
                              style={{ width:120, height:32, padding: '4px 8px', border: '1px solid var(--admin-border-color)', borderRadius: '4px', fontSize: '14px' }}
                            />
                            <input
                              type="text"
                              placeholder="https://..."
                              value={acc.__draftUrl}
                              onChange={(e) => onChangeSnsUrl(acc, e.target.value)}
                              style={{ width:'100%', height:32, padding: '4px 8px', border: '1px solid var(--admin-border-color)', borderRadius: '4px', fontSize: '14px', background: acc.__draftStatus === 'rejected' ? '#f5f5f5' : 'var(--admin-bg-input)' }}
                              disabled={acc.__draftStatus === 'rejected'}
                            />
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="admin-section__actions" style={{ justifyContent: 'flex-end', marginTop: 20 }}>
                <button
                  className="admin-section__button admin-section__button--secondary"
                  type="button"
                  onClick={downloadZip}
                >
                  첨부 이미지 다운로드
                </button>

                <button
                  className="admin-section__button"
                  type="button"
                  onClick={() => setConfirmCompleteOpen(true)}
                  disabled={loading || !allReady}
                  style={{ backgroundColor: loading || !allReady ? '#c7b8ea' : 'var(--admin-color-primary)', borderColor: loading || !allReady ? '#c7b8ea' : 'var(--admin-color-primary)' }}
                >
                  등록 완료
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {confirmCompleteOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
          <div style={{ background:'var(--admin-bg-panel)', padding: 24, width:440, borderRadius: 'var(--admin-card-radius)', boxShadow: 'var(--admin-shadow-xl)' }}>
            <h3 style={{ marginTop:0, color: 'var(--admin-text-main)' }}>등록 완료 확인</h3>
            <p style={{ color: 'var(--admin-text-body)', marginBottom:16, fontSize: '14px' }}>승인/반려, 팔로워 수, URL 입력 내용이 일괄 반영됩니다.</p>
            <div className="admin-section__actions" style={{ justifyContent:'flex-end' }}>
              <button className="admin-section__button admin-section__button--secondary" type="button" onClick={()=>setConfirmCompleteOpen(false)} disabled={loading}>
                취소
              </button>
              <button
                className="admin-section__button"
                type="button"
                onClick={async () => { setConfirmCompleteOpen(false); await handleComplete(); }}
                disabled={loading}
                style={{ backgroundColor: 'var(--admin-color-primary)', borderColor: 'var(--admin-color-primary)' }}
              >
                완료
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default InfluencerSNSAddPage;
